/**
 * Support Chat — Direct support chat accessible from floating button.
 * Messages go straight to the backend → admin dashboard.
 * No jobId required — backend auto-resolves to driver's most recent job.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  View, Text, StyleSheet, FlatList, TextInput,
  TouchableOpacity, KeyboardAvoidingView, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';
import { useSupportNotifications } from '../../services/support-notifications';

interface SupportMessage {
  id: string;
  jobId: string;
  messageText: string;
  senderType: string;
  recipientType: string;
  timestamp: string;
  failed?: boolean; // local optimistic send that didn't reach the server
  job?: { id: string; wooOrderId: string; productTitle: string };
}

export function SupportInboxScreen({ navigation }: any) {
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<FlatList>(null);
  const { markAllRead, refresh } = useSupportNotifications();

  const loadMessages = async () => {
    try {
      const { data } = await api.get('/messages/support');
      if (Array.isArray(data)) {
        // Preserve any failed local sends — a blind full-replace would silently wipe the
        // driver's undelivered message on the next 5s poll, making them think it went through.
        setMessages(prev => {
          const failedLocal = prev.filter(m => m.failed);
          return [...data, ...failedLocal];
        });
      }
    } catch {}
  };

  useEffect(() => {
    loadMessages();
    // Opening the inbox = the driver has now seen support: clear the badge and
    // sync the global notifier's last-seen pointer.
    markAllRead();
    refresh();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deliver = async (msgText: string, optimisticId: string) => {
    try {
      // No jobId — backend will auto-resolve to driver's most recent job
      await api.post('/messages/send', { recipientType: 'AGENT', messageText: msgText });
      // Success — drop the optimistic bubble; the reload brings back the server copy.
      setMessages(prev => prev.filter(m => m.id !== optimisticId));
      await loadMessages();
    } catch (err: any) {
      console.warn('Support send failed:', err?.response?.data || err.message);
      // Failure — keep the bubble visible, flagged failed + tappable to resend.
      setMessages(prev => prev.map(m => m.id === optimisticId ? { ...m, failed: true } : m));
    }
  };

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const optimistic: SupportMessage = {
      id: `m-${Date.now()}`,
      jobId: '',
      messageText: text.trim(),
      senderType: 'DRIVER',
      recipientType: 'AGENT',
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    const msgText = text.trim();
    setText('');
    await deliver(msgText, optimistic.id);
    setSending(false);
  };

  // Retry a failed send: re-flag it pending and re-deliver under the same bubble id.
  const resend = async (item: SupportMessage) => {
    if (!item.failed) return;
    setMessages(prev => prev.map(m => m.id === item.id ? { ...m, failed: false } : m));
    await deliver(item.messageText, item.id);
  };

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Commonplace Support</Text>
          <Text style={styles.headerSub}>We typically reply within a few minutes</Text>
        </View>
      </View>

      {/* Messages */}
      <FlatList
        ref={listRef}
        data={messages}
        keyExtractor={i => i.id}
        contentContainerStyle={styles.list}
        onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: true })}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <View style={styles.supportIcon}>
              <Ionicons name="headset" size={32} color={colors.white} />
            </View>
            <Text style={styles.emptyText}>Commonplace Support</Text>
            <Text style={styles.emptySub}>
              Send us a message and we'll get back to you as soon as possible.
            </Text>
          </View>
        }
        renderItem={({ item }) => {
          const isDriver = item.senderType === 'DRIVER';
          return (
            <View style={[styles.bubbleRow, isDriver ? styles.bubbleRowOut : styles.bubbleRowIn]}>
              {!isDriver && (
                <View style={styles.avatarSmall}>
                  <Ionicons name="headset" size={12} color={colors.white} />
                </View>
              )}
              <TouchableOpacity
                activeOpacity={item.failed ? 0.7 : 1}
                disabled={!item.failed}
                onPress={() => resend(item)}
                style={[styles.bubble, isDriver ? styles.bubbleOut : styles.bubbleIn, item.failed && { opacity: 0.6 }]}
              >
                <Text style={[styles.bubbleText, isDriver ? styles.bubbleTextOut : styles.bubbleTextIn]}>
                  {item.messageText}
                </Text>
                {item.failed ? (
                  <Text style={[styles.timestamp, styles.timestampOut, { color: '#ffd2d2' }]}>
                    Not delivered — tap to resend
                  </Text>
                ) : (
                  <Text style={[styles.timestamp, isDriver && styles.timestampOut]}>
                    {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          );
        }}
      />

      {/* Input Bar — always visible */}
      <View style={styles.inputBar}>
        <TextInput
          style={styles.textInput}
          value={text}
          onChangeText={setText}
          placeholder="Message support..."
          placeholderTextColor={RD.textFaint}
          multiline
          maxLength={500}
        />
        <TouchableOpacity
          style={[styles.sendBtn, !text.trim() && styles.sendBtnDisabled]}
          onPress={send}
          disabled={!text.trim() || sending}
        >
          <Ionicons name="arrow-up" size={20} color={colors.white} />
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container:       { flex: 1, backgroundColor: RD.bg },
  header:          { backgroundColor: RD.surface, paddingHorizontal: spacing.base, paddingTop: Platform.OS === 'ios' ? 56 : 36, paddingBottom: spacing.sm, flexDirection: 'row', alignItems: 'center', gap: 12, borderBottomWidth: 1, borderBottomColor: RD.border },
  backBtn:         { padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  headerCenter:    { flex: 1 },
  headerTitle:     { fontSize: 19, fontWeight: '700', color: RD.text, fontFamily: fonts.semiBold },
  headerSub:       { fontSize: 12, color: RD.textFaint, marginTop: 1 },

  list:            { padding: spacing.base, gap: 6, paddingBottom: 8, flexGrow: 1 },
  emptyState:      { alignItems: 'center', paddingTop: 80, gap: 10 },
  supportIcon:     { width: 64, height: 64, borderRadius: 32, backgroundColor: RD.maroon, justifyContent: 'center', alignItems: 'center', marginBottom: 8 },
  emptyText:       { fontSize: 18, fontWeight: '700', color: RD.text },
  emptySub:        { fontSize: 14, color: RD.textMuted, textAlign: 'center', paddingHorizontal: 40, lineHeight: 20 },

  bubbleRow:       { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 6 },
  bubbleRowIn:     { justifyContent: 'flex-start' },
  bubbleRowOut:    { justifyContent: 'flex-end' },
  avatarSmall:     { width: 28, height: 28, borderRadius: 14, backgroundColor: RD.maroon, justifyContent: 'center', alignItems: 'center' },
  bubble:          { maxWidth: '75%', borderRadius: 20, padding: 12 },
  bubbleIn:        { backgroundColor: RD.surface, borderWidth: 1, borderColor: RD.border, borderBottomLeftRadius: 6 },
  bubbleOut:       { backgroundColor: RD.ink, borderBottomRightRadius: 6 },
  bubbleText:      { fontSize: 15, lineHeight: 21 },
  bubbleTextIn:    { color: RD.text },
  bubbleTextOut:   { color: '#fff' },
  timestamp:       { fontSize: 10, color: RD.textFaint, marginTop: 4, alignSelf: 'flex-end' },
  timestampOut:    { color: 'rgba(255,255,255,0.55)' },

  inputBar:        { flexDirection: 'row', padding: spacing.md, backgroundColor: RD.surface, gap: 8, alignItems: 'flex-end', borderTopWidth: 1, borderTopColor: RD.border, paddingBottom: Platform.OS === 'ios' ? 30 : spacing.md },
  textInput:       { flex: 1, backgroundColor: RD.surface, borderWidth: 1.5, borderColor: RD.borderStrong, borderRadius: 14, paddingHorizontal: 15, paddingVertical: 13, fontSize: 15, color: RD.text, maxHeight: 100 },
  sendBtn:         { width: 44, height: 44, borderRadius: 14, backgroundColor: RD.ink, justifyContent: 'center', alignItems: 'center' },
  sendBtnDisabled: { backgroundColor: RD.g15 },
});
